/**
 * The standing left column — KANON `Detal klienta v3`, and since v3 it is **Przegląd's alone**.
 *
 * Four cards: the trainer's private note, attendance, the active plan, the goal. The IDENTITY that
 * used to head this column is in the top bar now, which is what lets the column be absent on five
 * tabs out of six without the page losing the client's name. None of the four decides anything;
 * `model/summaryColumn` already did.
 *
 * ── The note edits in place, and an empty save DELETES ────────────────────────────────────────
 * Mobile's rule, ported exactly: a non-empty save is `PUT /customer/{id}/note`, an empty save on an
 * existing note is `DELETE`, and an empty save with nothing to clear does nothing at all. The
 * sentence under the field says which of the three is about to happen, so the deletion is readable
 * before it is taken.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ClientStanding } from '~/entities/client';
import { motion } from 'motion/react';

import { screenMotion } from '~/shared/design-system/motion';
import { ReadoutRing } from '~/shared/ui/chart/ReadoutRing';
import ghost from '~/shared/ui/ghostButton.module.css';
import ink from '~/shared/ui/inkButton.module.css';
import { PlanCard, type PlanCardLink } from '~/shared/ui/PlanCard';
import { TrainingWeekStrip, type TrainingDayState } from '~/shared/ui/TrainingWeekStrip';

import { useSummaryColumn } from '../lib/useSummaryColumn';
import type { ClientDetailTab } from '../model/detailTabs';
import {
  ATTENDANCE_RECENT_DAYS,
  attendanceSubline,
  type AttendanceView,
} from '../model/attendance';
import {
  CLIENT_NOTE_MAX_LENGTH,
  type GoalView,
  type NoteView,
  type PlanView,
} from '../model/summaryColumn';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { EmptyState } from '~/shared/ui/EmptyState';
import {
  CalendarMarkIcon,
  GoalMarkIcon,
  MeasureMarkIcon,
  NoteMarkIcon,
  PlanMarkIcon,
} from '~/shared/ui/EmptyStateIcons';
import inlineButton from '~/shared/ui/inlineTextButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import eyebrows from './cardEyebrow.module.css';
import styles from './SummaryColumn.module.css';

/**
 * The goal ring, as v3 draws it: 62px across with a 7px band.
 *
 * Numbers rather than tokens because `RingChart` computes its geometry in JavaScript — see
 * `ReadoutRing`. `--sz-goal-ring` carries the same diameter for this column's own stylesheet, which
 * reserves the box, and 62 is the value it was read from.
 */
const GOAL_RING = { diameter: 62, thickness: 7 } as const;

/**
 * The states the FREKWENCJA legend names, in the order it names them.
 *
 * Three, not the roster panel's four: `/consistency` cannot report a session still ahead of the
 * client, so a *zaplanowana* entry would name a mark the strip can never draw.
 */
const WEEK_STATES: readonly TrainingDayState[] = ['done', 'missed', 'free'];

interface SummaryColumnProps {
  /**
   * The client whose column this is. The column makes its own reads — see `lib/useSummaryColumn`,
   * and the note there on why they are not the screen's.
   */
  readonly clientId: number;
  readonly onSaveNote: (body: string) => void;
  readonly noteSaving: boolean;
  readonly noteFailed: boolean;
  readonly reducedMotion: boolean;
  readonly onOpenTab: (tab: ClientDetailTab) => void;
  /**
   * Where the plan card leads, and what it is called there: this client's detail with the plan
   * named, which is the Kalendarz tab's plan grid. Assembled by the screen rather than here,
   * because the address needs the roster context the trainer arrived on and the name needs the
   * client's own — see `entities/client`'s `planCalendarLink`.
   *
   * `null` on a client with no active plan, and the empty card below is therefore not a link.
   */
  readonly planLink: PlanCardLink | null;
}

export function SummaryColumn({
  clientId,
  onSaveNote,
  noteSaving,
  noteFailed,
  reducedMotion,
  onOpenTab,
  planLink,
}: SummaryColumnProps) {
  const { t } = useTranslation();
  const { view, pending, attendanceFailed, complianceFailed, todayIso } = useSummaryColumn(clientId);

  return (
    /* A `complementary` landmark with its OWN name. The page has two complementary landmarks (this
       and the session panel), so distinct names are what tells them apart rather than a nicety. */
    <aside className={styles.column} aria-label={t('web:clientDetail.summaryLabel')}>
      <NoteCard
        note={view.note}
        pending={pending.note}
        saving={noteSaving}
        failed={noteFailed}
        reducedMotion={reducedMotion}
        onSave={onSaveNote}
      />

      {/* One card in one slot. A self-paced client has no adherence to fold (R15), so the standing
        * REPLACES the frekwencja card rather than sitting beside it — two cards would invite the
        * reader to compare a position against a percentage that is not there.
        *
        * The branch is the plan's MODE, which arrives with the read this whole screen is gated on.
        * It is deliberately not `view.standing !== null`: that is a figures-arrived test, and it
        * sent a self-paced client to the adherence fold whenever the compliance call was slow. */}
      {view.selfPaced ? (
        <StandingCard
          standing={view.standing}
          pending={pending.standing}
          failed={complianceFailed}
        />
      ) : (
        <AttendanceCard
          view={view.attendance}
          pending={pending.attendance}
          failed={attendanceFailed}
          todayIso={todayIso}
        />
      )}

      <PlanSection
        plan={view.plan}
        pending={pending.plan}
        link={planLink}
        reducedMotion={reducedMotion}
      />

      <GoalCard
        goal={view.goal}
        pending={pending.goal}
        reducedMotion={reducedMotion}
        onOpenTab={onOpenTab}
      />
    </aside>
  );
}

/**
 * The trainer's private note, read and edited in place.
 *
 * ── The card GROWS into the editor rather than jumping into it ────────────────────────────────
 * Owner, 1 September 2026: "zrób, żeby nie było takiej brzydkiej animacji, że nagle karta się tak
 * rozszerza". The read state is one line and a button; the editor is a four-row field and three
 * controls, so pressing `Edytuj` used to snap the card taller and shove the attendance, plan and
 * goal cards down the column in the same frame.
 *
 * Of the two ways out, this is the one that does not cost anything the rest of the time: the height
 * is ANIMATED rather than reserved. Reserving the editor's height would leave a hole in the column
 * for every client with no note — which is most of them, most of the time — to spare a movement
 * that happens when somebody deliberately opens an editor.
 *
 * The curve and the duration are `screenMotion('enter')`'s, taken as a whole transition rather than
 * copied as numbers, so this movement has the same character as every other movement in the app and
 * `prefers-reduced-motion` is honoured by the dictionary rather than by a stylesheet block that
 * cannot reach a Motion animation. Under the preference the duration is zero: the card resizes at
 * once, which is a state change and not a movement.
 *
 * `height` is the animated property, deliberately. Motion's `layout` would do it with transforms
 * and no per-frame layout, but it scales the box it is on — and this box's CONTENTS are swapped at
 * the same moment, so a transform-driven resize distorts a textarea into existence. One 360px card
 * with four children is a layout cost worth paying for a correct picture.
 *
 * The height is MEASURED rather than handed to Motion as `auto`. `animate={{ height: 'auto' }}`
 * looks like it should work and does nothing: Motion animates when the target changes, and `auto`
 * to `auto` is not a change — measured on the running app, where the card still moved the plan card
 * 80px in a single frame. So a layout effect reads the new content height after every swap and the
 * animation runs between two numbers.
 */
function NoteCard({
  note,
  pending,
  saving,
  failed,
  reducedMotion,
  onSave,
}: {
  note: NoteView;
  /** `GET /customer/{id}/note` is still open, so there is no body and no answer about one. */
  pending: boolean;
  saving: boolean;
  failed: boolean;
  reducedMotion: boolean;
  onSave: (body: string) => void;
}) {
  const { t } = useTranslation();
  const gate = useSkeletonGate(pending);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);

  const trimmed = draft.trim();
  // The same three-way rule mobile's sheet applies: save, clear, or nothing to do.
  const clears = trimmed.length === 0 && note.hasBody;
  const canSave = trimmed !== note.body.trim() && (trimmed.length > 0 || note.hasBody);

  /**
   * Leaving the editor — Cancel, Escape, AND Save.
   *
   * Focus has to be put back deliberately. `Edytuj` is REPLACED by the field, so the control that
   * comes back is a different DOM node than the one the trainer pressed, and every exit drops them
   * on `<body>` without this. A flag plus an effect rather than a call, because the button does not
   * exist yet at the moment the decision to leave is taken.
   *
   * A REF, not state, and the effect watches `editing` — the thing that actually changed. It was a
   * monotonic counter used as its own effect dependency, which is state that never reaches the
   * render and exists only to make an effect fire; `ClientDetailScreen`'s `handOffFocusToTab` is
   * the same errand written the same way, and one shape for it is one place to get it right.
   */
  const editRef = useRef<HTMLButtonElement>(null);
  const returnFocus = useRef(false);

  /**
   * The slot's height, in pixels, read after each content swap.
   *
   * `'auto'` only on the first pass, before anything has been measured — animating out of it is
   * what Motion cannot do, and starting from it means the card's initial render is not a movement.
   */
  const slotRef = useRef<HTMLDivElement>(null);
  const [slotHeight, setSlotHeight] = useState<number | 'auto'>('auto');
  useLayoutEffect(() => {
    // Runs AFTER the DOM holds the new state, so this is the height to travel TO while `animate`
    // still carries the one to travel FROM.
    const measured = slotRef.current?.scrollHeight;
    if (measured !== undefined) setSlotHeight(measured);
    // The GATE's two fields are deps because they are what swaps the slot's CONTENT: the wait
    // ending replaces a skeleton line with the note's own, and a slot measured before that swap
    // animates to the wrong number. They have to be the same booleans the branch below reads, or
    // the measurement happens on a frame the card is not drawing.
  }, [editing, note.body, note.hasBody, gate.waiting, gate.skeleton]);
  useEffect(() => {
    // Only on the way OUT of the editor, and only when this component asked — the card must not
    // grab focus on mount, and it must not grab it when the editor is being opened.
    if (editing || !returnFocus.current) return;
    returnFocus.current = false;
    editRef.current?.focus();
  }, [editing]);

  const closeEditor = () => {
    // Back to what the card currently HOLDS. On the save path that is the pre-save body for the
    // moment it takes the write to land, and it is never read while the editor is shut.
    setDraft(note.body);
    returnFocus.current = true;
    setEditing(false);
  };

  return (
    <section
      className={`${styles.card} ${styles.cardQuiet}`}
      aria-label={t('web:clientDetail.note.sectionLabel')}
    >
      <div className={styles.cardHead}>
        <em className={eyebrows.eyebrow}>{t('web:clientDetail.note.eyebrow')}</em>
        <em className={styles.cardAside}>{t('web:clientDetail.note.private')}</em>
      </div>

      {/* The box that changes size. `height: auto` is measured by Motion on every content swap, so
        * the card travels between its two heights instead of arriving at the second one. */}
      <motion.div
        ref={slotRef}
        className={styles.noteSlot}
        animate={{ height: slotHeight }}
        transition={screenMotion('enter', { reducedMotion }).transition}
      >
      {editing ? (
        <>
          <textarea
            className={styles.noteField}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Escape') return;
              // `preventDefault` is the protocol this app already uses for a consumed Escape: the
              // session panel listens on `window` and skips a key somebody nearer has answered.
              event.preventDefault();
              closeEditor();
            }}
            placeholder={note.placeholder}
            aria-label={t('web:clientDetail.note.editLabel')}
            maxLength={CLIENT_NOTE_MAX_LENGTH}
            rows={4}
            /* The field REPLACES the control the trainer just pressed, so without this focus lands
               on `<body>` and the keyboard path into the editor is a dead end. */
            autoFocus
          />
          <div className={styles.cardFoot}>
            <em className={styles.footNote}>
              {clears ? t('web:clientDetail.note.clearHint') : ''}
            </em>
            <button
              type="button"
              className={`${String(ghost.ghost)} ${String(styles.ghost)}`}
              onClick={closeEditor}
            >
              {t('clients.detail.overview.note.cancel')}
            </button>
            <button
              type="button"
              className={`${String(ink.ink)} ${String(styles.primarySm)}`}
              disabled={!canSave || saving}
              onClick={() => {
                onSave(trimmed);
                closeEditor();
              }}
            >
              {t('clients.detail.overview.note.save')}
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Three answers, not two. `note.hasBody` is false while the read is OPEN as well as
            * when the client has no note, and the card asserted "Brak notatki." in both — and
            * offered `Edytuj` on a body it did not have yet. The skeleton is one line where the
            * note's first line will be, so the slot measures the same either way. */}
          {gate.waiting ? (
            <p className={`${String(styles.noteBody)} ${String(styles.noteLineReserve)}`}>
              <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
              {gate.skeleton ? (
                <i className={`${String(skel.bar)} ${String(styles.skelNoteLine)}`} />
              ) : null}
            </p>
          ) : note.hasBody ? (
            <p className={`${String(styles.noteBody)} ${gate.reveal}`}>{note.body}</p>
          ) : (
            /* ACTION, not a bare absence: the note is written on this very card, and `Dodaj` in
             * the foot below is the way in. Inline, at the sentence's own italic type, because the
             * column has 39px less room than a no-scroll fit needs and a stacked mark would take
             * a row. */
            <EmptyState
              inline
              className={`${String(styles.noteBody)} ${String(styles.noteEmpty)} ${gate.reveal}`}
              mark={<NoteMarkIcon />}
              tone="action"
              title={t('clients.detail.overview.note.empty')}
            />
          )}
          <div className={styles.cardFoot}>
            <em className={styles.footNote}>{note.updatedText ?? ''}</em>
            <button
              type="button"
              ref={editRef}
              className={`${String(ghost.ghost)} ${String(styles.ghost)}`}
              // Refused until the card has a note to edit — the gate's wait, so the control does
              // not become pressable while the bars it belongs beside are still on screen. It
              // keeps its box either way, so the foot does not change shape.
              disabled={gate.waiting}
              onClick={() => {
                setDraft(note.body);
                setEditing(true);
              }}
            >
              {t('clients.detail.overview.note.edit')}
            </button>
          </div>
        </>
      )}
      </motion.div>

      {failed ? (
        <p className={styles.error} role="alert">
          {t('clients.detail.overview.note.saveError')}
        </p>
      ) : null}
    </section>
  );
}

/**
 * What stands where the frekwencja card stands, for a client training at their own pace.
 *
 * R16 names three things and this draws all three, in the requirement's own order of importance:
 * recency first (the load-bearing one), then position, then the completed/skipped balance over the
 * recent window. The wait is fourth and conditional.
 *
 * ── The balance is two figures, never a ratio ─────────────────────────────────────────────────
 * R16 is explicit that a completed-to-skipped ratio must not be a headline: it reads as a perfect
 * score for the client who simply stopped without declining anything, rewarding the least engaged
 * and penalising the one who says they are sitting a session out.
 *
 * ── It is drawn whether or not its figures arrived ────────────────────────────────────────────
 * Whether this card appears is decided by the plan's MODE, upstream, and nothing about a failed
 * request can change a plan's mode. The alternative that used to happen was worse than an
 * incomplete card: a failed read fell the client back to the attendance fold, which for an undated
 * plan reports a flawless 100%.
 */
function StandingCard({
  standing,
  pending,
  failed,
}: {
  readonly standing: ClientStanding | null;
  /** `/compliance` is still open. Separate from `failed` AND from an answer with no standing. */
  readonly pending: boolean;
  readonly failed: boolean;
}) {
  const { t, i18n } = useTranslation();
  // The platform's own relative wording: `numeric: 'auto'` gives "dziś" and "wczoraj" rather than
  // "0 dni temu".
  const relative = new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' });
  const gate = useSkeletonGate(pending);

  // `gate.waiting || standing === null`: on `standing === null` alone the loaded return below is
  // entered the instant `/compliance` answers, so the bars are abandoned mid-life and `reveal`
  // lands on a branch that is no longer the one being drawn.
  if (gate.waiting || standing === null) {
    return (
      /* THREE states behind one `null`, and until 2 September 2026 this card had two. `standing`
       * is null while the read is open, when it fails, AND when it answers with no active
       * assignment — and that third case printed "Wczytuję klienta…" for ever, because the
       * fallback branch assumed anything that was not a failure was still coming. */
      <section
        className={`${styles.card} ${styles.reserveStanding}`}
        aria-label={t('web:standing.eyebrow')}
        aria-busy={gate.waiting}
      >
        <div className={styles.cardHead}>
          <em className={eyebrows.eyebrow}>{t('web:standing.eyebrow')}</em>
        </div>
        {gate.waiting ? (
          <>
            <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
            {gate.skeleton ? (
              <>
                <i className={`${String(skel.bar)} ${String(styles.skelLead)}`} />
                <i className={`${String(skel.bar)} ${String(styles.skelLine)}`} />
                <i className={`${String(skel.bar)} ${String(styles.skelLineShort)}`} />
              </>
            ) : null}
          </>
        ) : failed ? (
          <p className={styles.cardNotice}>{t('web:standing.unavailable')}</p>
        ) : (
          /* NEUTRAL: a self-paced plan with no position to report yet is a window with nothing in
           * it, and the copy this app has for it is the attendance card's own. */
          <EmptyState
            mark={<CalendarMarkIcon />}
            tone="neutral"
            title={t('web:clientDetail.attendance.empty')}
          />
        )}
      </section>
    );
  }

  const { cycleIndex, cycleTotal } = standing.position;

  return (
    <section className={styles.card} aria-label={t('web:standing.eyebrow')}>
      <div className={styles.cardHead}>
        <em className={eyebrows.eyebrow}>{t('web:standing.eyebrow')}</em>
      </div>

      <p className={`${String(styles.standingLead)} ${gate.reveal}`}>
        {standing.recency.kind === 'trained'
          ? t('web:standing.lastTraining', {
              when: relative.format(-standing.recency.days, 'day'),
            })
          : standing.recency.kind === 'neverTrained'
            ? t('web:standing.neverTrained')
            : t('web:standing.awaiting')}
      </p>

      <p className={`${String(styles.standingLine)} ${gate.reveal}`}>
        {t('web:standing.position', { index: cycleIndex, total: cycleTotal })}
        {standing.waitingDays !== null && (
          <> · {t('web:standing.waiting', { count: standing.waitingDays })}</>
        )}
      </p>

      {/* The window the two figures below are counted over — ABOVE them, and their `<dl>`'s
        * accessible name. Underneath it put the scope after the thing it scopes in both channels
        * at once. */}
      <p className={`${String(styles.standingWindow)} ${gate.reveal}`} id="standing-window">
        {t('web:standing.windowCaption')}
      </p>

      <dl className={`${String(styles.standingBalance)} ${gate.reveal}`} aria-labelledby="standing-window">
        <div className={styles.standingFigure}>
          <dt className={styles.standingFigureLabel}>{t('web:standing.balanceCompleted')}</dt>
          <dd className={styles.standingFigureValue}>{standing.balance.completed}</dd>
        </div>
        <div className={styles.standingFigure}>
          <dt className={styles.standingFigureLabel}>{t('web:standing.balanceSkipped')}</dt>
          <dd className={styles.standingFigureValue}>{standing.balance.skipped}</dd>
        </div>
      </dl>
    </section>
  );
}

/**
 * FREKWENCJA: the fortnight as a hero figure, and the same fourteen days under it as one strip.
 *
 * ── The strip's CONTENT is the pre-v3 card's; its STYLING is v3's ─────────────────────────────
 * Owner, 1 September 2026, of v3's bare row of squares: "nie rozumiem tego rzędu kwadratów". His
 * instruction was to put back WHAT the old bar showed and keep how the card looks now. What came
 * back is a captioned window and a legend that names each state in words; the type, the spacing and
 * the card around them are untouched. The window itself is the fortnight rather than the old bar's
 * week, and the captions are dates rather than weekday letters — both are the second correction
 * below, not this one.
 *
 * The drawing is `shared/ui/TrainingWeekStrip`, which is the roster preview panel's strip promoted
 * on its second consumer. Neither surface owns the marks, the captions, the legend words or the
 * hover readout any more, so the two places in this app that draw a training week cannot drift.
 *
 * ── ONE window, said twice ────────────────────────────────────────────────────────────────────
 * The card carried three spans on 211px of height — a fortnight in the hero, a week in the strip
 * and a month in a sentence — and the owner cut it to one on 1 September 2026, in two steps.
 * "Zróbmy ostatnie 14 dni, skoro wyżej jest »4 z 7 sesji w ostatnich 14 dniach«" moved the strip
 * onto the hero's fortnight; "a ten tekst usuńmy" took the thirty-day sentence off the card.
 *
 * What is left is the figure and the days it counts, one under the other. The strip's caption
 * stays even though there is no longer a second window to tell it apart from: it is what says the
 * fourteen marks are the fourteen days just gone. The caption, the accessible name AND the strip's
 * column count all take their span from `ATTENDANCE_RECENT_DAYS`, the same constant the projection
 * slices by, so the words, the track and the marks cannot come apart.
 *
 * ── One row, dated at its ends ────────────────────────────────────────────────────────────────
 * Owner, 1 September 2026: "zrób te ostatnie 14 dni jako pasek koło siebie, a nie jeden pod
 * drugim." Fourteen marks in one row rather than two rows of seven, which is what takes the weekday
 * captions off this strip — fourteen letters at that column width order nothing. The dates under
 * the ends and the Mondays between them are the Trendy grid's answer to the same problem, and the
 * pointer names every cell in between.
 *
 * ── Three states, not the roster's four ───────────────────────────────────────────────────────
 * *zaplanowana* is not drawn, because this card reads `/consistency` and nothing in that read can
 * produce it — see `model/attendance`'s header for the two independent reasons. The roster's card
 * reads the calendar and draws all four.
 */
function AttendanceCard({
  view,
  pending,
  failed,
  todayIso,
}: {
  view: AttendanceView | null;
  pending: boolean;
  failed: boolean;
  todayIso: string;
}) {
  const { t } = useTranslation();
  // Three of the card's four states draw a sentence where the hero, the strip and the legend
  // stand. The card holds their height through all three, so the plan and goal cards under it do
  // not travel when the answer lands — see `.reserveAttendance`.
  const drawsFigures = view !== null && view.window.pct !== null;
  const gate = useSkeletonGate(pending);

  return (
    <section
      // The reserve comes off only when the figures are actually being DRAWN. `drawsFigures` alone
      // would drop it the moment `/consistency` answered, while the bars were still standing in it.
      className={`${styles.card} ${!gate.waiting && drawsFigures ? '' : styles.reserveAttendance}`}
      aria-label={t('web:clientDetail.attendance.eyebrow')}
      aria-busy={gate.waiting}
    >
      <div className={styles.cardHead}>
        <em className={eyebrows.eyebrow}>
          {t('web:clientDetail.attendance.eyebrowFortnight', { days: ATTENDANCE_RECENT_DAYS })}
        </em>
      </div>

      {gate.waiting ? (
        /* The card's own shape: the hero figure, the strip's caption, fourteen marks and the
         * legend. Same boxes, so the answer lands into them rather than under them. */
        <>
          <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
          {gate.skeleton ? (
            <>
              <i className={`${String(skel.bar)} ${String(styles.skelHero)}`} />
              <i className={`${String(skel.bar)} ${String(styles.skelCaption)}`} />
              <span className={styles.skelStrip}>
                {Array.from({ length: ATTENDANCE_RECENT_DAYS }, (_, cell) => (
                  <i key={cell} className={`${String(skel.bar)} ${String(styles.skelCell)}`} />
                ))}
              </span>
              <i className={`${String(skel.bar)} ${String(styles.skelLegend)}`} />
            </>
          ) : null}
        </>
      ) : view === null && failed ? (
        <p className={styles.cardNotice}>{t('web:clientDetail.attendance.unavailable')}</p>
      ) : view === null || view.window.pct === null ? (
        /* ONE empty card for the two ways of being empty, because they read the same.
         *
         * `view === null` past the notice above is a read that ANSWERED with nothing;
         * `window.pct === null` is a fortnight that held no scheduled training. Both are NEUTRAL:
         * they state a fact and ask for nothing, because a client can legitimately have a rest
         * fortnight and a client the server knows nothing about yet is not a client who missed
         * anything. They were two identical `EmptyState`s nine lines apart. */
        <EmptyState
          mark={<CalendarMarkIcon />}
          tone="neutral"
          title={t('web:clientDetail.attendance.empty')}
        />
      ) : (
        <>
          {/* KANON „Karty zadań" (24 September 2026): DAYS, not sessions — „5 z 14 dni z
            * treningiem", because `/consistency` gives one verdict per day — and under it how many
            * of them were missed and when the client last trained. */}
          <p className={`${String(styles.attHero)} ${gate.reveal}`}>
            <b className={styles.attFigure}>{view.recent.done}</b>
            <span className={styles.attHeroText}>
              {t('web:clientDetail.attendance.heroDaysOf', { days: ATTENDANCE_RECENT_DAYS })}
              <br />
              {t('web:clientDetail.attendance.heroDaysTrained')}
            </span>
          </p>
          <p className={`${String(styles.attSub)} ${gate.reveal}`}>{attendanceSubline(view, todayIso, (key, values) => t(key, values))}</p>

          {view.recentDays.length === 0 ? null : (
            <TrainingWeekStrip
              days={view.recentDays}
              columns={ATTENDANCE_RECENT_DAYS}
              captions="ends"
              states={WEEK_STATES}
              label={t('web:clientDetail.attendance.weekLabel', {
                days: ATTENDANCE_RECENT_DAYS,
                done: view.recentDays.filter((day) => day.state === 'done').length,
                missed: view.recentDays.filter((day) => day.state === 'missed').length,
                free: view.recentDays.filter((day) => day.state === 'free').length,
              })}
            />
          )}
        </>
      )}
    </section>
  );
}

/**
 * The client's current plan: `shared/ui/PlanCard` once there is one, and this column's own empty
 * card when there is not.
 *
 * The drawing moved to `shared/` on 1 September 2026, on the owner's instruction that the roster
 * preview shows the same card; `shared/` carries no `t` and no domain vocabulary, so the card takes
 * finished strings. Those strings are `entities/client`'s `planCardWords`, reached through the
 * projection — the roster's panel calls the same function, which is what stops one card's eyebrow,
 * scheduling line or week counter from being spelled a second way.
 *
 * So this component decides nothing about the plan at all. It picks between three states: the card,
 * the wait, and the empty.
 *
 * The `Kalendarz →` link that used to sit in this card's head is GONE, also on the owner's word,
 * and it stays gone. What took its place is the WHOLE CARD: owner, 2 September 2026, "po prostu po
 * kliknięciu na całą kartę ma tam przenosić". Removing the link had left the plan's cycle grid with
 * no entry from the app at all — the grid's plan picker lives in the grid's own header, so it only
 * appears once you are already on it.
 *
 * The card is the link; the EMPTY card below is not, and neither is a card whose plan reached us
 * with no assignment id. `link` is `null` in both cases and `PlanCard` then draws no anchor, no
 * pointer and no focus stop — a card that looks pressable and goes nowhere is the one outcome worse
 * than a card that does not look pressable.
 */
function PlanSection({
  plan,
  pending,
  link,
  reducedMotion,
}: {
  plan: PlanView | null;
  pending: boolean;
  link: PlanCardLink | null;
  reducedMotion: boolean;
}) {
  const { t } = useTranslation();
  const gate = useSkeletonGate(pending);

  // The wait travels with the branch: on `plan === null` alone the `PlanCard` below is returned
  // the moment `/plan` answers, 300ms before the gate retires the bars it is replacing.
  if (gate.waiting || plan === null) {
    return (
      <section
        className={`${styles.card} ${styles.reservePlan} ${gate.waiting ? '' : styles.centred}`}
        aria-label={t('web:clientDetail.plan.eyebrow')}
        aria-busy={gate.waiting}
      >
        <div className={styles.cardHead}>
          <em className={eyebrows.eyebrow}>{t('web:clientDetail.plan.eyebrow')}</em>
        </div>
        {/* "Ten klient nie ma aktywnego planu" is a statement about the client, and it was being
          * made while the read that answers it was still open. Until it lands the card draws the
          * plan card's own shape — cover, name, meta line, progress bar — and it holds the plan
          * card's height in every one of the three states. */}
        {gate.waiting ? (
          <>
            <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
            {gate.skeleton ? (
              <>
                <i className={`${String(skel.bar)} ${String(styles.skelCover)}`} />
                <i className={`${String(skel.bar)} ${String(styles.skelLead)}`} />
              </>
            ) : null}
          </>
        ) : (
          /* ACTION: a client with no plan needs one, and the hint says where it is assigned. */
          <EmptyState
            mark={<PlanMarkIcon />}
            tone="action"
            title={t('web:clientDetail.plan.empty')}
            hint={t('web:clientDetail.plan.emptyHint')}
          />
        )}
      </section>
    );
  }

  return (
    <PlanCard
      className={gate.reveal}
      plan={{
        name: plan.name,
        coverImageUrl: plan.coverImageUrl,
        categoryKey: plan.categoryKey,
        categoryLabel: plan.words.categoryLabel,
        weeksLabel: plan.words.weeksLabel,
        endsLabel: plan.words.endsLabel,
        paused: plan.paused,
        progressPct: plan.progressPct,
        endDateLabel: plan.endDateLabel,
      }}
      eyebrow={plan.words.eyebrow}
      styleLine={plan.words.styleLine}
      pausedLabel={plan.words.pausedLabel}
      size="column"
      link={link}
      reducedMotion={reducedMotion}
    />
  );
}

/**
 * The goal — one ring, and the three figures under it on the honest denominator (start → target).
 *
 * ── No `Ukończ` / `Anuluj` here any more ─────────────────────────────────────────────────────
 * KANON „Karty zadań" (24 September 2026) draws the card without them, and the owner kept them only
 * if dropping them cost a function the mobile app has. It does not: the mobile app completes and
 * cancels no goal anywhere. Completing a goal is the „Cel do odnowienia" task card's act now
 * (`features/client-goal`); cancelling one has no surface in this app, which the round's report
 * says.
 */
function GoalCard({
  goal,
  pending,
  reducedMotion,
  onOpenTab,
}: {
  goal: GoalView | null;
  pending: boolean;
  reducedMotion: boolean;
  onOpenTab: (tab: ClientDetailTab) => void;
}) {
  const { t } = useTranslation();
  const gate = useSkeletonGate(pending);

  // Same as the plan card above: the early return is the WAIT branch, so it has to stand until
  // the gate says the wait is over, not until `/goal` answers.
  if (gate.waiting || goal === null) {
    return (
      <section
        className={`${styles.card} ${styles.reserveGoal} ${gate.waiting ? '' : styles.centred}`}
        aria-label={t('web:clientDetail.goal.eyebrow')}
        aria-busy={gate.waiting}
      >
        <div className={styles.cardHead}>
          <em className={eyebrows.eyebrow}>{t('web:clientDetail.goal.eyebrow')}</em>
        </div>
        {/* Same rule as the plan card above: "brak aktywnego celu" is an answer, and the card does
          * not give it before `/goal` has. The wait draws the goal card's own shape: the ring,
          * the name and the three figures beside it. */}
        {gate.waiting ? (
          <>
            <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
            {gate.skeleton ? (
              <span className={styles.skelGoalRow}>
                <i className={`${String(skel.bar)} ${String(skel.disc)} ${String(styles.skelRing)}`} />
                <span className={styles.skelGoalText}>
                  <i className={`${String(skel.bar)} ${String(styles.skelLead)}`} />
                  <i className={`${String(skel.bar)} ${String(styles.skelLine)}`} />
                  <i className={`${String(skel.bar)} ${String(styles.skelLineShort)}`} />
                </span>
              </span>
            ) : null}
          </>
        ) : (
          /* ACTION, and the hint is honest about where the act lives: the goal builder is not
           * designed for this app and the owner's list names it as pending, so the sentence names
           * the mobile app rather than offering a dead button. */
          <EmptyState
            mark={<GoalMarkIcon />}
            tone="action"
            title={t('web:clientDetail.goal.empty')}
            hint={t('web:clientDetail.goal.emptyHint')}
          />
        )}
      </section>
    );
  }

  /**
   * An UNMEASURED goal and a goal measured at zero are not the same thing, and they ask the trainer
   * to do opposite things: one needs a reading taken, the other needs the programme looked at.
   *
   * The RING is not what tells them apart, and trying to make it was the mistake. Both draw the same
   * empty arc, because `progressPct` is `null` exactly when `gap` is set and 0 is the only honest
   * length for an arc nobody has measured. What separates them is stated in type: the figure inside
   * the ring is a dash rather than `0%`, the ring's accessible name is the missing reading rather
   * than a percentage, and the card carries an inline action line naming which reading is missing
   * and where it is taken. A real 0% prints its zero and carries no such line.
   */
  const unmeasured = goal.gap !== null;
  const pct = goal.progressPct ?? 0;

  return (
    <section className={styles.card} aria-label={t('web:clientDetail.goal.eyebrow')}>
      <div className={styles.cardHead}>
        <em className={eyebrows.eyebrow}>{t('web:clientDetail.goal.eyebrow')}</em>
        {goal.deadlineLabel === null ? null : (
          <em className={`${String(styles.cardAside)} ${gate.reveal}`}>{goal.deadlineLabel}</em>
        )}
      </div>

      <div className={`${String(styles.goalRow)} ${gate.reveal}`}>
        <ReadoutRing
          // `pct` is already 0 on the unmeasured branch — `progressPct` is `null` exactly when
          // `gap` is set — so the ring takes it straight. What `unmeasured` still decides is the
          // FIGURE and the words under it, two lines down.
          percent={pct}
          diameter={GOAL_RING.diameter}
          thickness={GOAL_RING.thickness}
          colour="var(--chart-series-goal)"
          figure={unmeasured ? '—' : `${String(Math.round(pct))}%`}
          label={
            unmeasured
              ? goal.gap?.note ?? goal.title
              : t('web:clientDetail.goal.ringLabel', {
                  title: goal.title,
                  pct: Math.round(pct),
                })
          }
          reducedMotion={reducedMotion}
        />
        <b className={styles.goalTitle}>{goal.title}</b>
      </div>

      {/* start · teraz · cel on one inset strip under the ring — the canon's honest denominator. */}
      {goal.figures.length === 0 ? null : (
        <dl className={`${String(styles.goalFigures)} ${gate.reveal}`}>
          {goal.figures.map((entry, index) => (
            <div
              key={entry.label}
              className={`${styles.goalFigure} ${index === 1 ? styles.goalFigureNow : ''}`}
            >
              <dt className={styles.goalLabel}>{entry.label}</dt>
              <dd className={styles.goalValue}>{entry.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {goal.gap === null ? null : (
        /* "Brak pomiaru — pasek pokaże postęp po pierwszym zapisie" is an ACTION state living
         * INSIDE a drawn card, so its mark rides on the sentence's own line: a stacked one would
         * take a row off a column that already misses its no-scroll fit by 39px. The tab link at
         * the end of the sentence is the act, which is why `title` takes a node. */
        <EmptyState
          inline
          className={styles.goalUnmeasured}
          mark={<MeasureMarkIcon />}
          tone="action"
          title={
            <span>
              {goal.gap.note}{' '}
              <button
                type="button"
                className={inlineButton.inlineTextButton}
                onClick={() => {
                  if (goal.gap !== null) onOpenTab(goal.gap.tab);
                }}
              >
                {goal.gap.linkLabel}
              </button>
            </span>
          }
        />
      )}

    </section>
  );
}
